import {
  EventRescheduleConfirmDialog,
  type RescheduleChoice,
} from "@/components/calendars/EventRescheduleConfirmDialog.tsx";
import { type ReactNode, useCallback, useRef, useState } from "react";

type AskArgs = {
  oldStart: Date;
  oldEnd: Date;
  newStart: Date;
  newEnd: Date;
  isAllDay: boolean;
  allowSendWithoutEmail: boolean;
};

/**
 * Promise-returning wrapper around EventRescheduleConfirmDialog so useEventDrag
 * can await the user's choice in linear flow.
 *
 * Resolves with the user's RescheduleChoice. If the user dismisses the dialog
 * (Escape / backdrop click) it resolves with `{ kind: "discard" }` — the
 * least-surprising default since drag-and-then-dismiss should not commit a
 * change.
 */
export function useRescheduleConfirmPromise(): {
  dialog: ReactNode;
  askForConfirm: (args: AskArgs) => Promise<RescheduleChoice>;
} {
  const [args, setArgs] = useState<AskArgs | null>(null);
  const resolveRef = useRef<((choice: RescheduleChoice) => void) | null>(null);

  const askForConfirm = useCallback((nextArgs: AskArgs) => {
    return new Promise<RescheduleChoice>((resolve) => {
      resolveRef.current = resolve;
      setArgs(nextArgs);
    });
  }, []);

  const settle = useCallback((choice: RescheduleChoice) => {
    setArgs(null);
    resolveRef.current?.(choice);
    resolveRef.current = null;
  }, []);

  const dialog = args ? (
    <EventRescheduleConfirmDialog
      open
      oldStart={args.oldStart}
      oldEnd={args.oldEnd}
      newStart={args.newStart}
      newEnd={args.newEnd}
      isAllDay={args.isAllDay}
      allowSendWithoutEmail={args.allowSendWithoutEmail}
      onChoose={(choice) => settle(choice)}
      onClose={() => settle({ kind: "discard" })}
    />
  ) : null;

  return { dialog, askForConfirm };
}
